// bookState.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Book, BooksState } from '../../types/bookTypes'
import {
  fetchBooksThunk,
  createBookThunk,
  deleteBookThunk,
  updateBookThunk,
} from './booksThunk'

const initialState: BooksState = {
  books: [],
  currentBook: null,
  currentIndex: null,
  isLoading: false,
  error: null,
  currentPage: 1,
}

export const bookSlice = createSlice({
  name: 'book',
  initialState,
  reducers: {
    setCurrentBook: (state, action: PayloadAction<Book>) => {
      state.currentBook = action.payload
    },
    startLoading: (state) => {
      state.isLoading = true
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
    },
    updateCurrentPage: (
      state,
      action: PayloadAction<{ bookId: string; currentPage: number }>
    ) => {
      const { bookId, currentPage } = action.payload
      const book = state.books.find((b) => b.id === bookId)
      if (book) {
        book.currentPage = currentPage
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBooksThunk.pending, (state) => {
        state.isLoading = true
      })
      .addCase(
        fetchBooksThunk.fulfilled,
        (state, action: PayloadAction<Book[] | undefined>) => {
          state.books = action.payload ?? []
          state.isLoading = false
        }
      )
      .addCase(fetchBooksThunk.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message ?? 'Failed to load books'
      })
      .addCase(createBookThunk.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(
        createBookThunk.fulfilled,
        (state, action: PayloadAction<Book | undefined>) => {
          if (action.payload) {
            state.books.unshift(action.payload)
            state.isLoading = false
          }
        }
      )
      .addCase(createBookThunk.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message ?? 'Failed to create book'
      })
      .addCase(deleteBookThunk.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(deleteBookThunk.fulfilled, (state, action: any) => {
        if (action.payload) {
          const { bookId } = action.payload
          state.books = state.books.filter((book) => book.bookId !== bookId)
        }
        state.isLoading = false
      })
      .addCase(deleteBookThunk.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message ?? 'Failed to delete book'
      })

      .addCase(updateBookThunk.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(
        updateBookThunk.fulfilled,
        (state, action: PayloadAction<any>) => {
          if (action.payload) {
            const book = action.payload
            const index = state.books.findIndex((b) => b.bookId === book.bookId)
            if (index !== -1) {
              state.books[index] = {
                ...state.books[index],
                ...book,
              }
            }
          }
          state.isLoading = false
        }
      )
      .addCase(updateBookThunk.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message ?? 'Failed to delete book'
      })
  },
})

export const { setCurrentBook, startLoading, setError } = bookSlice.actions

export default bookSlice.reducer
